/**
 * feat-235 — the REVERSE view (mockup §8): which Tracks a Lesson is in, shown on
 * the Lesson's own panel in the Lessons tab. This is the only change the Lessons
 * tab takes.
 *
 * Without it, an author editing "Reading the Guest" has no way to know three
 * tracks depend on it. The POSITION is shown because it is the one thing the
 * Tracks tab would otherwise hide from you here, and because it is AC20 stated
 * as a sentence: a lesson in two tracks shows both, and reordering it in one does
 * not move it in the other.
 *
 * READ-ONLY on purpose. These chips are navigation, not a second editor: a
 * removable chip would put a membership write behind a control that cannot show
 * the ordering it is disturbing, and would give two screens the power to fire the
 * same last-write-wins `PUT …/lessons`.
 */

import React from 'react';
import type { LearnTrack } from '../../../../services/quizContentAdminService';
import { QUIZ_TERMS, termCount } from '../../quizTerminology';
import { lessonMemberships, ordinal } from './trackGraph';
import { formatReleaseInstant } from '../lessonSchedule';

/**
 * feat-256 — is there a release date worth talking about?
 *
 * Design notes §7.16 phrase the condition as `available_at !== null`, past or
 * future. This adds the unparseable case, which that phrasing does not reach:
 * garbage is treated as NO date, matching what the meta strip does one pane over
 * (it falls back to the `＋ Schedule` affordance rather than render `Invalid
 * Date`). Rendering "a release date won't change that" beside a control showing
 * no release date at all would be the strip contradicting itself.
 */
const hasParseableSchedule = (iso: string | null | undefined): boolean =>
  formatReleaseInstant(iso) !== null;

export const LessonTracksStrip: React.FC<{
  lessonId: string;
  tracks: LearnTrack[];
  /** Jump to the Tracks tab with that track selected. */
  onOpenTrack?: (trackId: string) => void;
  /** feat-256 — the Lesson's release instant (ISO-8601 UTC) or `null`. OPTIONAL
   *  for the same reason `tracks` is: every pre-feat-256 caller and fixture keeps
   *  rendering this strip exactly as it did, with no schedule note. */
  availableAt?: string | null;
}> = ({ lessonId, tracks, onOpenTrack, availableAt }) => {
  const memberships = lessonMemberships(lessonId, tracks);

  return (
    <div data-testid="lesson-tracks-strip">
      <span className="block text-xs font-bold text-inkMuted mb-1.5">
        {memberships.length === 0
          ? `In no ${QUIZ_TERMS.track.singular}`
          : `In ${termCount(memberships.length, 'track')}`}
      </span>
      {memberships.length === 0 ? (
        <>
          <p className="text-xs text-inkMuted">
            Nobody sees this {QUIZ_TERMS.lesson.singular} yet. That's fine if it's a draft — file it from
            the {QUIZ_TERMS.track.plural} tab when it's ready.
          </p>
          {/* feat-256 (7.16) — a SEPARATE <p>, never a sentence appended to the one
              above. Two reasons: the original string stays byte-intact for the
              tests already asserting it, and a test can assert this sentence
              without phrase-matching across a wrapped line — which asserts where
              the line broke rather than what it says.

              Rendered whenever a date is set, PAST OR FUTURE, and independent of
              `active`. A past-dated untracked Lesson also reaches nobody, which is
              the whole point: without this, setting a date on an untracked Lesson
              reads as "done".

              It is NOT suppressed while the Lesson is Hidden, and that was asked
              directly. The three gates — active, schedule, track membership — are
              independent, and clearing one does not clear another. The chip says
              `Held · …` and never mentions Tracks; this sentence never mentions
              Hidden. Suppressing it would re-create the exact bug the AC28
              reversal fixed: the author flips Active feeling finished and only
              THEN discovers the Track problem, with the note appearing on the flip
              as though flipping Active caused it.

              Not rendered when there is no date — 41 Lessons do not need the
              sentence. `text-preview` ties it to the chip and the `Later` pill so
              the three schedule surfaces read as one idea. */}
          {hasParseableSchedule(availableAt) && (
            <p
              data-testid="lesson-untracked-schedule-note"
              className="text-xs text-preview font-semibold mt-1.5"
            >
              A release date won't change that — the date will pass and this{' '}
              {QUIZ_TERMS.lesson.singular} still won't appear until it's in a {QUIZ_TERMS.track.singular}.
            </p>
          )}
        </>
      ) : (
        <>
          <div className="flex gap-2 flex-wrap items-center">
            {memberships.map(({ track, position }) => (
              <button
                key={track.id}
                type="button"
                data-testid={`lesson-track-chip-${track.id}`}
                onClick={() => onOpenTrack?.(track.id)}
                className="text-[11.5px] font-bold rounded-full px-2.5 py-1 border border-softBorder bg-white text-inkMuted hover:bg-cream"
              >
                {track.title || '(untitled)'} · {ordinal(position)}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-inkMuted mt-1.5">
            Its position is per {QUIZ_TERMS.track.singular} — moving it in one doesn't move it in
            another.
          </p>
        </>
      )}
    </div>
  );
};

export default LessonTracksStrip;
